import type { ImportJob, Source } from '@casetrace/shared';

export type SourceWithLatestJob = {
  source: Source;
  latestJob: ImportJob | null;
};

export const SOURCE_STATUS_LABEL: Record<Source['status'], string> = {
  pending: 'Venter',
  importing: 'Importerer',
  completed: 'Ferdig',
  failed: 'Feilet',
};

export const JOB_STATUS_LABEL: Record<ImportJob['status'], string> = {
  queued: 'I kø',
  running: 'Kjører',
  completed: 'Fullført',
  failed: 'Feilet',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function pathTail(path: string): string {
  const normalized = path.replace(/\\/g, '/');
  const parts = normalized.split('/');
  return parts[parts.length - 1] ?? normalized;
}

export function uploadSummary(source: Source): string | null {
  const metadata = source.metadata;

  const documentUpload = isRecord(metadata.documentUpload) ? metadata.documentUpload : undefined;
  const files = Array.isArray(documentUpload?.files) ? documentUpload.files : undefined;
  if (files && files.length > 0) {
    return files.length === 1 ? '1 fil lastet opp' : `${files.length} filer lastet opp`;
  }

  const iphoneSms = isRecord(metadata.iphoneSms) ? metadata.iphoneSms : undefined;
  const conversations = Array.isArray(iphoneSms?.conversations)
    ? iphoneSms.conversations
    : undefined;
  if (conversations && conversations.length > 0) {
    return conversations.length === 1
      ? '1 samtale valgt'
      : `${conversations.length} samtaler valgt`;
  }

  const upload = isRecord(metadata.upload) ? metadata.upload : undefined;
  const objectPath = typeof upload?.objectPath === 'string' ? upload.objectPath : undefined;
  if (objectPath) {
    return `Fil: ${pathTail(objectPath)}`;
  }

  return null;
}

export function sortJobs(jobs: ImportJob[]): ImportJob[] {
  return [...jobs].sort((a, b) => {
    const aTime = a.startedAt ?? a.finishedAt ?? '';
    const bTime = b.startedAt ?? b.finishedAt ?? '';
    return bTime.localeCompare(aTime);
  });
}

export function bySource(sources: Source[], jobs: ImportJob[]): SourceWithLatestJob[] {
  const sortedJobs = sortJobs(jobs);
  return sources.map((source) => ({
    source,
    latestJob: sortedJobs.find((job) => job.sourceId === source.id) ?? null,
  }));
}
